import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { z } from "zod";
import { supabase, razorpayEndpoints } from "@/lib/supabaseClient";
import { toaster } from "@/components/ui/toaster";

// =====================================================================
// 1. ZOD RUNTIME VALIDATION SCHEMAS
// =====================================================================

export const SeekerContactSchema = z.object({
  consultationType: z.string().min(1, "Please choose a consultation journey"),
  seekerName: z.string().min(2, "Full name must be at least 2 characters"),
  seekerEmail: z.string().email("Please provide a valid email address"),
  seekerPhone: z
    .string()
    .min(8, "Phone number must be at least 8 digits")
    .regex(/^[0-9+\-()\s]*$/, "Only numeric and spacing characters permitted in phone number"),
});

export const SeekerBirthDataSchema = z.object({
  birthDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Birth date must be formatted as YYYY-MM-DD"),
  birthTime: z.string().min(5, "Precise birth time is required"),
  birthPlace: z.string().min(2, "Birth place must specify city and state"),
});

export const ConsultationBookingSchema = SeekerContactSchema.merge(SeekerBirthDataSchema).extend({
  bookingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Booking date must be formatted as YYYY-MM-DD"),
  bookingTime: z.string().min(1, "Please select an available time slot"),
  notes: z.string().optional(),
  
  // Optional columns for Kundali Matching
  boyName: z.string().optional(),
  boyBirthDate: z.string().optional(),
  boyBirthTime: z.string().optional(),
  boyBirthPlace: z.string().optional(),
  girlName: z.string().optional(),
  girlBirthDate: z.string().optional(),
  girlBirthTime: z.string().optional(),
  girlBirthPlace: z.string().optional(),
});

export const CourseEnrolmentSchema = z.object({
  courseId: z.string().uuid("Invalid course identification code"),
  studentName: z.string().min(2, "Full name must be at least 2 characters"),
  studentEmail: z.string().email("Please provide a valid email address"),
  studentPhone: z
    .string()
    .min(8, "Phone number must be at least 8 digits")
    .regex(/^[0-9+\-()\s]*$/, "Only numeric and spacing characters permitted"),
});

// Types inferred from Zod schemas
export type ConsultationBookingInput = z.infer<typeof ConsultationBookingSchema>;
export type CourseEnrolmentInput = z.infer<typeof CourseEnrolmentSchema>;

// =====================================================================
// 2. TANSTACK QUERIES (Data Fetching with Caching)
// =====================================================================

// Fetch active consultation types/categories
export const useConsultationTypesQuery = () => {
  return useQuery({
    queryKey: ["consultation_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consultation_types")
        .select("*")
        .eq("is_active", true)
        .order("price");

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// Fetch the standard weekly availability hours
export const useWorkingScheduleQuery = () => {
  return useQuery({
    queryKey: ["working_schedule"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("working_schedule")
        .select("*")
        .order("day_of_week");

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// Fetch future holiday calendar exceptions/overrides
export const useHolidaysQuery = () => {
  return useQuery({
    queryKey: ["holidays"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("holidays")
        .select("*")
        .order("date");

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// Fetch active course syllabi and catalogs
export const useCoursesQuery = () => {
  return useQuery({
    queryKey: ["courses"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .eq("is_active", true)
        .order("created_at");

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// =====================================================================
// 3. TANSTACK MUTATIONS (State Modifying Requests)
// =====================================================================

// Submit a personal consultation booking request
export const useCreateBookingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ConsultationBookingInput) => {
      // Perform runtime schema check
      const validated = ConsultationBookingSchema.parse(input);

      const { data, error } = await supabase
        .from("consultation_bookings")
        .insert({
          consultation_type: validated.consultationType,
          seeker_name: validated.seekerName,
          seeker_email: validated.seekerEmail,
          seeker_phone: validated.seekerPhone,
          birth_date: validated.birthDate,
          birth_time: validated.birthTime,
          birth_place: validated.birthPlace,
          notes: validated.notes,
          booking_date: validated.bookingDate,
          booking_time: validated.bookingTime,
          payment_gateway: "razorpay",
          status: "pending_payment",
          
          // Dedicated fields for Kundali Matching
          boy_name: validated.boyName || null,
          boy_birth_date: validated.boyBirthDate || null,
          boy_birth_time: validated.boyBirthTime || null,
          boy_birth_place: validated.boyBirthPlace || null,
          girl_name: validated.girlName || null,
          girl_birth_date: validated.girlBirthDate || null,
          girl_birth_time: validated.girlBirthTime || null,
          girl_birth_place: validated.girlBirthPlace || null,
        })
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      // Invalidate active slot selections to trigger a re-evaluation of availability
      queryClient.invalidateQueries({ queryKey: ["working_schedule"] });
      queryClient.invalidateQueries({ queryKey: ["booked_slots"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bookings"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bookings_paged"] });
      toaster.create({
        title: "Booking Requested",
        description: "Your consultation slot has been reserved successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Booking Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

type RazorpayEntityType = "consultation" | "course";

type RazorpayOrderResponse = {
  order: {
    id: string;
    amount: number;
    currency: string;
    receipt: string;
    status: string;
    attempts: number;
    notes?: Record<string, string>;
  };
  record: any;
  keyId: string;
  entityType: RazorpayEntityType;
};

type VerifyPaymentPayload =
  | {
      entityType: "consultation";
      bookingId: string;
      razorpay_order_id: string;
      razorpay_payment_id: string;
      razorpay_signature: string;
    }
  | {
      entityType: "course";
      enrollmentId: string;
      razorpay_order_id: string;
      razorpay_payment_id: string;
      razorpay_signature: string;
    };

export type VerifyPaymentResult = {
  success: boolean;
  record?: any;
};

export const createRazorpayOrder = async (bookingId: string): Promise<RazorpayOrderResponse> => {
  const response = await fetch(razorpayEndpoints.createOrder, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ entityType: "consultation", bookingId }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || "Unable to create Razorpay order");
  }

  return response.json();
};

export const createCourseRazorpayOrder = async (enrollmentId: string): Promise<RazorpayOrderResponse> => {
  const response = await fetch(razorpayEndpoints.createOrder, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ entityType: "course", enrollmentId }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || "Unable to create Razorpay order for course");
  }

  return response.json();
};

export const verifyRazorpayPayment = async (payload: VerifyPaymentPayload): Promise<VerifyPaymentResult> => {
  const response = await fetch(razorpayEndpoints.verifyPayment, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || "Failed to verify Razorpay payment");
  }

  return response.json();
};

export const verifyCourseRazorpayPayment = async (payload: Omit<VerifyPaymentPayload, "entityType" | "bookingId"> & { enrollmentId: string }): Promise<VerifyPaymentResult> => {
  return verifyRazorpayPayment({
    entityType: "course",
    enrollmentId: payload.enrollmentId,
    razorpay_order_id: payload.razorpay_order_id,
    razorpay_payment_id: payload.razorpay_payment_id,
    razorpay_signature: payload.razorpay_signature,
  });
};

// Request enrolment into a cohort study course program
export const useCreateEnrolmentMutation = () => {
  return useMutation({
    mutationFn: async (input: CourseEnrolmentInput) => {
      const validated = CourseEnrolmentSchema.parse(input);

      const { data, error } = await supabase
        .from("course_enrollments")
        .insert({
          course_id: validated.courseId,
          student_name: validated.studentName,
          student_email: validated.studentEmail,
          student_phone: validated.studentPhone,
          status: "pending_payment"
        })
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      toaster.create({
        title: "Enrollment Requested",
        description: "Your request has been recorded. Check details to complete the transfer.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Enrollment Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Signal that payment transfer screenshot was sent on WhatsApp
export const useSignalPaymentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, isCourse }: { bookingId: string; isCourse: boolean }) => {
      const table = isCourse ? "course_enrollments" : "consultation_bookings";
      
      const { data, error } = await supabase
        .from(table)
        .update({ whatsapp_screenshot_sent: true })
        .eq("id", bookingId)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_, variables) => {
      const typeKey = variables.isCourse ? "course_enrollments" : "consultation_bookings";
      queryClient.invalidateQueries({ queryKey: [typeKey] });
      toaster.create({
        title: "Payment Signalled",
        description: "Vedic support team notified successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Notification Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// =====================================================================
// 4. ADMIN DASHBOARD OPERATIONS
// =====================================================================

// Fetch all existing booked slots to prevent double-booking (for seekers view)
export const useBookedSlotsQuery = () => {
  return useQuery({
    queryKey: ["booked_slots"],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc("get_booked_slots");

      if (error) throw new Error(error.message);
      return data || [];
    },
    refetchInterval: 5000, // Poll every 5 seconds to ensure real-time slot locking
  });
};

// Fetch all consultation bookings (Admin-only view)
export const useAdminBookingsQuery = () => {
  return useQuery({
    queryKey: ["admin_bookings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consultation_bookings")
        .select("*")
        .order("booking_date", { ascending: false });

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// Fetch consultation bookings with database filtering and pagination (Admin-only paged view)
export const useAdminBookingsPagedQuery = (params: {
  search: string;
  status: string;
  type: string;
  dateFilter: string;
  page: number;
  pageSize: number;
}) => {
  return useQuery({
    queryKey: ["admin_bookings_paged", params.search, params.status, params.type, params.dateFilter, params.page, params.pageSize],
    queryFn: async () => {
      let query = supabase
        .from("consultation_bookings")
        .select("*", { count: "exact" });

      if (params.status && params.status !== "all") {
        query = query.eq("status", params.status);
      }

      if (params.type && params.type !== "all") {
        query = query.eq("consultation_type", params.type);
      }

      if (params.search) {
        const cleanSearch = params.search.trim();
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cleanSearch);
        if (isUuid) {
          query = query.eq("id", cleanSearch);
        } else {
          query = query.or(`seeker_name.ilike.%${cleanSearch}%,seeker_email.ilike.%${cleanSearch}%,seeker_phone.ilike.%${cleanSearch}%`);
        }
      }

      if (params.dateFilter && params.dateFilter !== "all") {
        const todayStr = new Date().toISOString().split("T")[0];
        
        if (params.dateFilter === "today") {
          query = query.eq("booking_date", todayStr);
        } else if (params.dateFilter === "tomorrow") {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          const tomorrowStr = tomorrow.toISOString().split("T")[0];
          query = query.eq("booking_date", tomorrowStr);
        } else if (params.dateFilter === "this_week") {
          const nextWeek = new Date();
          nextWeek.setDate(nextWeek.getDate() + 7);
          const nextWeekStr = nextWeek.toISOString().split("T")[0];
          query = query.gte("booking_date", todayStr).lte("booking_date", nextWeekStr);
        } else if (params.dateFilter === "next_week") {
          const startNextWeek = new Date();
          startNextWeek.setDate(startNextWeek.getDate() + 7);
          const startNextWeekStr = startNextWeek.toISOString().split("T")[0];
          
          const endNextWeek = new Date();
          endNextWeek.setDate(endNextWeek.getDate() + 14);
          const endNextWeekStr = endNextWeek.toISOString().split("T")[0];
          
          query = query.gte("booking_date", startNextWeekStr).lte("booking_date", endNextWeekStr);
        } else if (params.dateFilter === "past") {
          query = query.lt("booking_date", todayStr);
        }
      }

      query = query.order("booking_date", { ascending: false });

      const from = (params.page - 1) * params.pageSize;
      const to = from + params.pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw new Error(error.message);
      return { data: data || [], totalCount: count || 0 };
    },
  });
};

// Fetch all course enrollments (Admin-only view)
export const useAdminEnrollmentsQuery = () => {
  return useQuery({
    queryKey: ["admin_enrollments"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_enrollments")
        .select("*, courses(title, price)")
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// Fetch course enrollments with database filtering and pagination (Admin-only paged view)
export const useAdminEnrollmentsPagedQuery = (params: {
  search: string;
  status: string;
  dateFilter: string;
  page: number;
  pageSize: number;
}) => {
  return useQuery({
    queryKey: ["admin_enrollments_paged", params.search, params.status, params.dateFilter, params.page, params.pageSize],
    queryFn: async () => {
      let query = supabase
        .from("course_enrollments")
        .select("*, courses(title, price)", { count: "exact" });

      if (params.status && params.status !== "all") {
        query = query.eq("status", params.status);
      }

      if (params.search) {
        const cleanSearch = params.search.trim();
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cleanSearch);
        if (isUuid) {
          query = query.eq("id", cleanSearch);
        } else {
          query = query.or(`student_name.ilike.%${cleanSearch}%,student_email.ilike.%${cleanSearch}%,student_phone.ilike.%${cleanSearch}%`);
        }
      }

      if (params.dateFilter && params.dateFilter !== "all") {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayStartISO = todayStart.toISOString();

        if (params.dateFilter === "today") {
          const todayEnd = new Date();
          todayEnd.setHours(23, 59, 59, 999);
          query = query.gte("created_at", todayStartISO).lte("created_at", todayEnd.toISOString());
        } else if (params.dateFilter === "tomorrow") {
          const tomorrowStart = new Date();
          tomorrowStart.setDate(tomorrowStart.getDate() + 1);
          tomorrowStart.setHours(0, 0, 0, 0);
          
          const tomorrowEnd = new Date();
          tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
          tomorrowEnd.setHours(23, 59, 59, 999);
          
          query = query.gte("created_at", tomorrowStart.toISOString()).lte("created_at", tomorrowEnd.toISOString());
        } else if (params.dateFilter === "this_week") {
          const nextWeek = new Date();
          nextWeek.setDate(nextWeek.getDate() + 7);
          query = query.gte("created_at", todayStartISO).lte("created_at", nextWeek.toISOString());
        } else if (params.dateFilter === "next_week") {
          const startNextWeek = new Date();
          startNextWeek.setDate(startNextWeek.getDate() + 7);
          startNextWeek.setHours(0, 0, 0, 0);
          
          const endNextWeek = new Date();
          endNextWeek.setDate(endNextWeek.getDate() + 14);
          endNextWeek.setHours(23, 59, 59, 999);
          
          query = query.gte("created_at", startNextWeek.toISOString()).lte("created_at", endNextWeek.toISOString());
        } else if (params.dateFilter === "past") {
          query = query.lt("created_at", todayStartISO);
        }
      }

      query = query.order("created_at", { ascending: false });

      const from = (params.page - 1) * params.pageSize;
      const to = from + params.pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw new Error(error.message);
      return { data: data || [], totalCount: count || 0 };
    },
  });
};

// Fetch all aggregated client customer index profiles
export const useAdminClientsQuery = () => {
  return useQuery({
    queryKey: ["admin_clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .order("total_bookings", { ascending: false });

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// Fetch all client query logs
export const useAdminClientQueriesQuery = () => {
  return useQuery({
    queryKey: ["admin_client_queries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_queries")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// Update booking status (approve or reject)
export const useUpdateBookingStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "approved" | "rejected" | "pending_payment" }) => {
      const { data, error } = await supabase
        .from("consultation_bookings")
        .update({ status })
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin_bookings"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bookings_paged"] });
      queryClient.invalidateQueries({ queryKey: ["admin_clients"] });
      toaster.create({
        title: "Booking Updated",
        description: `Consultation status changed to ${data.status} successfully.`,
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Status Update Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Delete consultation booking
export const useDeleteBookingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("consultation_bookings")
        .delete()
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_bookings"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bookings_paged"] });
      queryClient.invalidateQueries({ queryKey: ["booked_slots"] });
      queryClient.invalidateQueries({ queryKey: ["admin_clients"] });
      toaster.create({
        title: "Booking Deleted",
        description: "The consultation booking has been removed successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Delete Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Update consultation booking details
export const useUpdateBookingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      id: string;
      seeker_name: string;
      seeker_email: string;
      seeker_phone: string;
      consultation_type: string;
      booking_date: string;
      booking_time: string;
      birth_date: string;
      birth_time: string;
      birth_place: string;
      notes?: string | null;
      status: "approved" | "rejected" | "pending_payment";
      admin_notes?: string | null;
    }) => {
      const { id, ...updates } = payload;
      const { data, error } = await supabase
        .from("consultation_bookings")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_bookings"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bookings_paged"] });
      queryClient.invalidateQueries({ queryKey: ["booked_slots"] });
      queryClient.invalidateQueries({ queryKey: ["admin_clients"] });
      toaster.create({
        title: "Booking Saved",
        description: "The consultation details have been updated successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Save Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Create a new consultation booking manually (Admin control)
export const useCreateBookingAdminMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      seeker_name: string;
      seeker_email: string;
      seeker_phone: string;
      consultation_type: string;
      booking_date: string;
      booking_time: string;
      birth_date: string;
      birth_time: string;
      birth_place: string;
      notes?: string | null;
      status: "approved" | "rejected" | "pending_payment";
      admin_notes?: string | null;
    }) => {
      const { data, error } = await supabase
        .from("consultation_bookings")
        .insert(payload)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_bookings"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bookings_paged"] });
      queryClient.invalidateQueries({ queryKey: ["booked_slots"] });
      queryClient.invalidateQueries({ queryKey: ["admin_clients"] });
      toaster.create({
        title: "Booking Created",
        description: "The new manual consultation booking has been created.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Creation Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Update course enrollment status (approve or reject)
export const useUpdateEnrollmentStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "approved" | "rejected" | "pending_payment" }) => {
      const { data, error } = await supabase
        .from("course_enrollments")
        .update({ status })
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin_enrollments"] });
      queryClient.invalidateQueries({ queryKey: ["admin_enrollments_paged"] });
      queryClient.invalidateQueries({ queryKey: ["admin_clients"] });
      toaster.create({
        title: "Enrollment Updated",
        description: `Course enrollment status changed to ${data.status} successfully.`,
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Status Update Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Fetch all courses for Admin
export const useAdminCoursesQuery = () => {
  return useQuery({
    queryKey: ["admin_courses"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// Fetch all consultation types for Admin
export const useAdminConsultationTypesQuery = () => {
  return useQuery({
    queryKey: ["admin_consultation_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consultation_types")
        .select("*")
        .order("price");

      if (error) throw new Error(error.message);
      return data;
    },
  });
};

// Create Course Mutation
export const useCreateCourseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newCourse: any) => {
      const { data, error } = await supabase
        .from("courses")
        .insert([newCourse])
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_courses"] });
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      toaster.create({
        title: "Course Created",
        description: "New spiritual cohort course listed successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Course Creation Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Update Course Mutation
export const useUpdateCourseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase
        .from("courses")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_courses"] });
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      toaster.create({
        title: "Course Updated",
        description: "Vedic course information has been saved successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Update Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Delete Course Mutation
export const useDeleteCourseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("courses")
        .delete()
        .eq("id", id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_courses"] });
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      toaster.create({
        title: "Course Removed",
        description: "Vedic course has been deleted from records.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Deletion Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Create Consultation Type Mutation
export const useCreateConsultationTypeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newType: any) => {
      const { data, error } = await supabase
        .from("consultation_types")
        .insert([newType])
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_consultation_types"] });
      queryClient.invalidateQueries({ queryKey: ["consultation_types"] });
      toaster.create({
        title: "Journey Created",
        description: "New spiritual consultation journey listed successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Creation Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Update Consultation Type Mutation
export const useUpdateConsultationTypeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase
        .from("consultation_types")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_consultation_types"] });
      queryClient.invalidateQueries({ queryKey: ["consultation_types"] });
      toaster.create({
        title: "Journey Updated",
        description: "Consultation parameters saved successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Update Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// Delete Consultation Type Mutation
export const useDeleteConsultationTypeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("consultation_types")
        .delete()
        .eq("id", id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_consultation_types"] });
      queryClient.invalidateQueries({ queryKey: ["consultation_types"] });
      toaster.create({
        title: "Journey Removed",
        description: "Consultation category deleted successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Deletion Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// =====================================================================
// 4. BANK DETAILS QUERY AND MUTATIONS
// =====================================================================

export const useBankDetailsQuery = () => {
  return useQuery({
    queryKey: ["bank_details"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bank_details")
        .select("*")
        .eq("is_active", true)
        .limit(1);

      if (error) throw new Error(error.message);
      return data?.[0] || null;
    },
  });
};

export const useAdminBankDetailsQuery = () => {
  return useQuery({
    queryKey: ["admin_bank_details"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bank_details")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);
      return data || [];
    },
  });
};

export const useCreateBankDetailsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newDetails: {
      title?: string;
      account_holder: string;
      bank_name: string;
      branch_name?: string | null;
      account_number: string;
      ifsc_code: string;
      upi_id: string;
      upi_number?: string | null;
      paypal_email?: string | null;
      paypal_link?: string | null;
      swift_code?: string | null;
      custom_instructions?: string | null;
      is_active?: boolean;
    }) => {
      if (newDetails.is_active) {
        await supabase
          .from("bank_details")
          .update({ is_active: false })
          .neq("id", "00000000-0000-0000-0000-000000000000");
      }

      const { data, error } = await supabase
        .from("bank_details")
        .insert([newDetails])
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank_details"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bank_details"] });
      toaster.create({
        title: "Payment Method Added",
        description: "New payment configuration profile created successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Creation Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useUpdateBankDetailsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase
        .from("bank_details")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank_details"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bank_details"] });
      toaster.create({
        title: "Bank Credentials Saved",
        description: "Official seeker payment details updated successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Update Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useSetActiveBankDetailsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (activeId: string) => {
      // 1. Deactivate all existing profiles
      const { error: deactivateError } = await supabase
        .from("bank_details")
        .update({ is_active: false })
        .neq("id", activeId);

      if (deactivateError) throw new Error(deactivateError.message);

      // 2. Activate target profile
      const { data, error: activateError } = await supabase
        .from("bank_details")
        .update({ is_active: true })
        .eq("id", activeId)
        .select()
        .single();

      if (activateError) throw new Error(activateError.message);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["bank_details"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bank_details"] });
      toaster.create({
        title: "Active Payment Section Changed",
        description: `"${data.title || data.bank_name || 'Selected payment method'}" is now live for all seekers.`,
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Activation Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useDeleteBankDetailsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("bank_details")
        .delete()
        .eq("id", id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank_details"] });
      queryClient.invalidateQueries({ queryKey: ["admin_bank_details"] });
      toaster.create({
        title: "Payment Method Removed",
        description: "Payment method profile deleted successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Deletion Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// =====================================================================
// 5. REVIEWS QUERY AND MUTATIONS
// =====================================================================

export const useReviewsQuery = () => {
  return useQuery({
    queryKey: ["reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);
      return data || [];
    },
  });
};

export const useAdminReviewsQuery = () => {
  return useQuery({
    queryKey: ["admin_reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);
      return data || [];
    },
  });
};

export const useCreateReviewMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newReview: any) => {
      const { data, error } = await supabase
        .from("reviews")
        .insert([newReview])
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      queryClient.invalidateQueries({ queryKey: ["admin_reviews"] });
      toaster.create({
        title: "Review Registered",
        description: "Testimonial review has been listed successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Registration Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useUpdateReviewMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase
        .from("reviews")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      queryClient.invalidateQueries({ queryKey: ["admin_reviews"] });
      toaster.create({
        title: "Review Saved",
        description: "Testimonial review details updated successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Update Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useDeleteReviewMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("reviews")
        .delete()
        .eq("id", id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      queryClient.invalidateQueries({ queryKey: ["admin_reviews"] });
      toaster.create({
        title: "Review Removed",
        description: "Testimonial has been deleted from records.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Deletion Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// =====================================================================
// 6. LANDING STATS QUERY AND MUTATIONS
// =====================================================================

export const useLandingStatsQuery = () => {
  return useQuery({
    queryKey: ["landing_stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("landing_stats")
        .select("*")
        .order("sort_order");

      if (error) throw new Error(error.message);
      return data || [];
    },
  });
};

export const useUpdateLandingStatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase
        .from("landing_stats")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing_stats"] });
      toaster.create({
        title: "Statistic Updated",
        description: "Landing metrics have been updated successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Update Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

// =====================================================================
// 7. GEMSTONES QUERY AND MUTATIONS
// =====================================================================

export const usePublicGemstonesQuery = () => {
  return useQuery({
    queryKey: ["public_gemstones"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gemstones")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);
      return data || [];
    },
  });
};

export const useAdminGemstonesQuery = () => {
  return useQuery({
    queryKey: ["admin_gemstones"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gemstones")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);
      return data || [];
    },
  });
};

export const useCreateGemstoneMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newGem: {
      title: string;
      subtitle: string;
      copy: string;
      price?: number | null;
      image?: string | null;
      images?: string[] | null;
      image_alt?: string | null;
      glow?: string;
      is_active?: boolean;
      youtube_url?: string | null;
    }) => {
      const { data, error } = await supabase
        .from("gemstones")
        .insert([newGem])
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_gemstones"] });
      queryClient.invalidateQueries({ queryKey: ["public_gemstones"] });
      toaster.create({
        title: "Gemstone Registered",
        description: "The certified gemstone has been uploaded and listed successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Registration Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useUpdateGemstoneMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase
        .from("gemstones")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_gemstones"] });
      queryClient.invalidateQueries({ queryKey: ["public_gemstones"] });
      toaster.create({
        title: "Gemstone Catalog Updated",
        description: "The certified gemstone details have been saved.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Update Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useDeleteGemstoneMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("gemstones")
        .delete()
        .eq("id", id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_gemstones"] });
      queryClient.invalidateQueries({ queryKey: ["public_gemstones"] });
      toaster.create({
        title: "Gemstone Removed",
        description: "The gemstone has been deleted from your inventory.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Deletion Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useSiteSettingsQuery = () => {
  return useQuery({
    queryKey: ["site_settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*");
      if (error) throw new Error(error.message);
      return data;
    },
  });
};

export const useUpdateSiteSettingMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { data, error } = await supabase
        .from("site_settings")
        .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" })
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site_settings"] });
      toaster.create({
        title: "Setting Saved",
        description: "Your system configuration settings have been updated.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Failed to Save Setting",
        description: error.message,
        type: "error",
      });
    },
  });
};

// =====================================================================
// BLOG POSTS — Admin & Public queries + mutations
// =====================================================================

/** Admin: fetch ALL posts (drafts + published) */
export const useAdminBlogPostsQuery = () => {
  return useQuery({
    queryKey: ["admin_blog_posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, slug, title, excerpt, content, category, status, is_featured, published_at, created_at, cover_image_url, tags, author_name")
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });
};

/** Public: fetch published posts only */
export const usePublicBlogPostsQuery = (category?: string) => {
  return useQuery({
    queryKey: ["public_blog_posts", category],
    queryFn: async () => {
      let query = supabase
        .from("blog_posts")
        .select("id, slug, title, excerpt, category, cover_image_url, tags, author_name, published_at, is_featured")
        .eq("status", "published")
        .order("published_at", { ascending: false });
      if (category) query = query.eq("category", category);
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return data;
    },
  });
};

/** Public: fetch a single published post by slug */
export const usePublicBlogPostQuery = (slug: string) => {
  return useQuery({
    queryKey: ["public_blog_post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    enabled: !!slug,
  });
};

export const useCreateBlogPostMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (post: {
      slug: string;
      title: string;
      excerpt?: string;
      content?: string;
      cover_image_url?: string | null;
      category?: string;
      tags?: string[];
      author_name?: string;
      youtube_url?: string | null;
      status: "draft" | "published";
      is_featured?: boolean;
    }) => {
      const { data, error } = await supabase
        .from("blog_posts")
        .insert([post])
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_blog_posts"] });
      queryClient.invalidateQueries({ queryKey: ["public_blog_posts"] });
      toaster.create({
        title: "Blog Post Created",
        description: "Your new blog post has been saved successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Failed to Create Post",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useUpdateBlogPostMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase
        .from("blog_posts")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_blog_posts"] });
      queryClient.invalidateQueries({ queryKey: ["public_blog_posts"] });
      toaster.create({
        title: "Blog Post Updated",
        description: "The blog post has been saved successfully.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Update Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useDeleteBlogPostMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("blog_posts")
        .delete()
        .eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_blog_posts"] });
      queryClient.invalidateQueries({ queryKey: ["public_blog_posts"] });
      toaster.create({
        title: "Blog Post Deleted",
        description: "The blog post has been permanently removed.",
        type: "success",
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: "Deletion Failed",
        description: error.message,
        type: "error",
      });
    },
  });
};

export const useAdminVisitorTrackingQuery = (page: number, searchQuery: string) => {
  const PAGE_SIZE = 10;
  return useQuery({
    queryKey: ["admin_visitor_sessions", page, searchQuery],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let query = supabase
        .from("visitor_sessions")
        .select("*", { count: "exact" });

      if (searchQuery) {
        query = query.or(`ip_address.ilike.%${searchQuery}%,identifier.ilike.%${searchQuery}%,location.ilike.%${searchQuery}%,user_agent.ilike.%${searchQuery}%`);
      }

      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data: sessions, count, error: sessionsError } = await query
        .order("created_at", { ascending: false })
        .range(from, to);

      if (sessionsError) throw new Error(sessionsError.message);

      const sessionIds = sessions?.map((s) => s.id) || [];
      let eventsBySessionId: Record<string, any[]> = {};

      if (sessionIds.length > 0) {
        const { data: events, error: eventsError } = await supabase
          .from("visitor_events")
          .select("*")
          .in("session_id", sessionIds)
          .order("created_at", { ascending: false });

        if (eventsError) throw new Error(eventsError.message);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        eventsBySessionId = (events || []).reduce((acc: Record<string, any[]>, event) => {
          if (!acc[event.session_id]) acc[event.session_id] = [];
          acc[event.session_id].push(event);
          return acc;
        }, {});
      }

      const enrichedSessions = (sessions || []).map((session) => ({
        ...session,
        events: eventsBySessionId[session.id] || [],
      }));

      return {
        sessions: enrichedSessions,
        total: count || 0,
        page,
        totalPages: Math.ceil((count || 0) / PAGE_SIZE),
      };
    },
  });
};

export const useAdminSitePagesQuery = () => {
  return useQuery({
    queryKey: ["admin_site_pages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_pages")
        .select("*")
        .order("slug");
      if (error) throw error;
      return data;
    },
  });
};

export const useUpdateSitePageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ slug, title, content }: { slug: string; title: string; content: string }) => {
      const { data, error } = await supabase
        .from("site_pages")
        .update({ title, content, updated_at: new Date().toISOString() })
        .eq("slug", slug)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin_site_pages"] });
      queryClient.invalidateQueries({ queryKey: ["site_page", variables.slug] });
    },
  });
};
